import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import type { FenLine, FenPose } from '../../logic/story/fen';
import type { SceneLayoutContext } from '../../scenes/SceneComponent';
import { animate, BoardPanel, captionInset, COLOURS, ease, layoutButtonRow, TitlePlaque, type BrassButton } from '../board/index';
import { placeFen, type FenPlacement } from './fenPlacement';
import { body, button } from './text';

/**
 * Fen the fox, the road's host: he steps in, says his line on a little
 * board with his name on a plaque, and steps out again. Hidden between
 * lines so the reels stay clear.
 */
export class FenHost extends Container {
  private readonly fen = new Sprite();
  private readonly bubble = new Container();
  private readonly board = new BoardPanel();
  private readonly tail = new Graphics();
  private readonly plaque = new TitlePlaque('FEN', 14);
  private readonly body = new Container();
  private ctx: SceneLayoutContext | null = null;
  private place: FenPlacement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private showing = 0;
  private asking = false;
  private withButtons = false;

  constructor(private readonly poses: (pose: FenPose) => Texture | null, floor: Texture | null) {
    super();
    this.board.setFloor(floor);
    this.bubble.addChild(this.tail, this.board, this.body, this.plaque);
    this.bubble.eventMode = 'static';
    this.bubble.cursor = 'pointer';
    this.bubble.on('pointertap', () => {
      if (!this.asking) this.hide();
    });
    this.fen.label = 'fen';
    this.addChild(this.fen, this.bubble);
    this.visible = false;
  }

  get talking(): boolean {
    return this.visible;
  }

  /** True when Fen stands beside the reels (wide screens); false when he's
   *  in the reels' corner (phones), where his board covers part of them. */
  get beside(): boolean {
    return this.place?.tail === 'below';
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.place = placeFen(ctx);
    if (this.visible) this.arrange();
  }

  /** Say a line; it clears itself after a few seconds, or on a tap. */
  say(line: FenLine): void {
    if (this.asking) return;
    this.fill(line.text, line.pose, null);
    this.show();
    this.hideTimer = setTimeout(() => this.hide(), Math.max(3500, line.text.length * 55));
  }

  /** Ask the player something with two answers: resolves true for the
   *  action (right), false for the other (left). Stays up until answered,
   *  or until {@link settle} answers for them. */
  ask(text: string, pose: FenPose, cancel: string, action: string): Promise<boolean> {
    this.asking = true;
    return new Promise((resolve) => {
      const done = (yes: boolean) => {
        if (!this.asking) return;
        this.asking = false;
        this.settleAsk = null;
        this.hide();
        resolve(yes);
      };
      this.settleAsk = done;
      this.fill(text, pose, {
        cancel: button(cancel, 'wood', () => done(false), 16),
        action: button(action, 'gold', () => done(true), 16),
      });
      this.show();
    });
  }

  private settleAsk: ((yes: boolean) => void) | null = null;

  /** Answer an open question for the player (they spun instead). */
  settle(yes: boolean): void {
    this.settleAsk?.(yes);
  }

  hide(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = null;
    if (!this.visible) return;
    const run = ++this.showing;
    const p = this.place;
    void animate(220, (t) => {
      if (run !== this.showing) return;
      this.alpha = 1 - t;
      if (p) this.fen.y = p.y + p.size * 0.3 * t;
    }).then(() => {
      if (run === this.showing) this.visible = false;
    });
  }

  private fill(text: string, pose: FenPose, buttons: { cancel: BrassButton; action: BrassButton } | null): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = null;
    const tex = this.poses(pose) ?? this.poses('talking');
    if (tex) this.fen.texture = tex;
    this.body.removeChildren().forEach((c) => c.destroy());
    const width = this.place?.bubble.width ?? 260;
    const words = body(text, width < 240 ? 14 : 15, { width: width - 24, align: 'left' });
    words.position.set(12, 18);
    this.body.addChild(words);
    this.withButtons = buttons !== null;
    if (buttons) {
      const y = Math.round(words.y + words.height + 10);
      buttons.cancel.resize(buttons.cancel.buttonWidth, 44);
      buttons.action.resize(buttons.action.buttonWidth, 44);
      rowByWords(buttons.cancel, buttons.action, 12, y, width - 24);
      this.body.addChild(buttons.cancel, buttons.action);
    }
  }

  private show(): void {
    const run = ++this.showing;
    this.visible = true;
    this.arrange();
    const p = this.place;
    if (!p) return;
    this.alpha = 0;
    void animate(320, (t) => {
      if (run !== this.showing) return;
      this.alpha = Math.min(1, t * 2);
      this.fen.y = p.y + p.size * 0.3 * (1 - ease.outCubic(t));
      this.bubble.scale.set(0.85 + 0.15 * ease.outBack(t));
    });
  }

  private arrange(): void {
    const p = this.place;
    if (!p) return;
    const tex = this.fen.texture;
    this.fen.scale.set(p.size / Math.max(1, tex.width, tex.height));
    this.fen.position.set(p.x, p.y);
    const w = p.bubble.width;
    const h = Math.round(this.body.height + (this.withButtons ? 34 : 30));
    this.board.resize(w, h);
    this.plaque.position.set(38, 0);
    const top = p.tail === 'below' ? p.bubble.y - h - 14 : p.bubble.y;
    let maxBottom = (this.ctx?.height ?? 9999) - 8;
    // On phones the board is over the reels: keep it above the frame's
    // bottom edge and the SPIN button (a tall board, such as the welcome
    // with its two buttons, rises instead), so SPIN stays in sight.
    if (p.tail === 'left' && this.ctx) maxBottom = Math.min(maxBottom, this.ctx.machineBottom - 4, this.ctx.spinButtonY - 46);
    const by = Math.max(8, Math.min(top, maxBottom - h));
    this.bubble.position.set(p.bubble.x, by);
    this.tail.clear();
    if (p.tail === 'left') {
      // The tail points at Fen's face wherever the board ends up.
      const ty = Math.max(12, Math.min(h - 30, Math.round(p.y + p.size * 0.3 - by)));
      this.tail.poly([2, ty, -12, ty + 12, 2, ty + 20]).fill(COLOURS.floor).stroke({ color: COLOURS.brass, width: 2 });
    } else {
      const tx = Math.min(w - 30, Math.max(20, p.x + p.size * 0.5 - p.bubble.x));
      this.tail.poly([tx - 10, h - 2, tx, h + 14, tx + 10, h - 2]).fill(COLOURS.floor).stroke({ color: COLOURS.brass, width: 2 });
    }
  }
}

/**
 * Cancel on the left, the action on the right, each plate as wide as its
 * words need and the room left over shared between them. On a narrow board
 * (a phone) a long cancel such as "Play without Fen" gets the room it needs
 * instead of half; when even that won't fit, the row splits by the words
 * and the captions shrink a little to fit their plates.
 */
export function rowByWords(cancel: BrassButton, action: BrassButton, x: number, y: number, width: number, gap = 10): void {
  const room = width - gap;
  const need = (b: BrassButton) => b.captionWidth + captionInset(b.buttonHeight) * 2 + 8;
  const nc = need(cancel);
  const na = need(action);
  const spare = room - nc - na;
  const even = nc <= room / 2 && na <= room / 2;
  const cw = Math.round(even ? room / 2 : spare >= 0 ? nc + spare / 2 : (room * nc) / (nc + na));
  if (cw <= 0 || cw >= room) {
    layoutButtonRow(cancel, action, x, y, width, gap);
    return;
  }
  cancel.resize(cw);
  action.resize(room - cw);
  cancel.position.set(x, y);
  action.position.set(x + cw + gap, y);
}
